"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export type ShowcaseFrame = {
  src: string;
  label: string;
  alt: string;
};

export const SEASON_FRAMES: ShowcaseFrame[] = [
  {
    src: "/landing/landscape.jpg",
    label: "Your yard",
    alt: "Original yard photo before redesign",
  },
  {
    src: "/landing/spring.jpg",
    label: "Spring",
    alt: "Yard redesigned, shown in spring",
  },
  {
    src: "/landing/summer.jpg",
    label: "Summer",
    alt: "Yard redesigned, shown in summer",
  },
  {
    src: "/landing/fall.jpg",
    label: "Fall",
    alt: "Yard redesigned, shown in fall",
  },
  {
    src: "/landing/winter.jpg",
    label: "Winter",
    alt: "Yard redesigned, shown in winter",
  },
];

const DEFAULT_CYCLE_MS = 3500;

interface SeasonalShowcaseProps {
  /** Frames to cycle through. Defaults to the four-season set. */
  frames?: ShowcaseFrame[];
  /** Dwell time per frame, in milliseconds. */
  cycleMs?: number;
  className?: string;
}

/**
 * Auto-cycling image reel: one yard photo crossfading through alternate
 * renders of the same scene (seasons by default, before/after when given two
 * frames). Crossfade (not a wipe) because the source photo and the generated
 * images have slightly offset compositions.
 */
export function SeasonalShowcase({
  frames = SEASON_FRAMES,
  cycleMs = DEFAULT_CYCLE_MS,
  className,
}: SeasonalShowcaseProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  // Bumped on manual selection so the auto-advance timer restarts even when the
  // user re-picks the frame that is already showing.
  const [restart, setRestart] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || reducedMotion) return;
    const timer = setTimeout(
      () => setActive((i) => (i + 1) % frames.length),
      cycleMs
    );
    return () => clearTimeout(timer);
  }, [active, paused, reducedMotion, restart, frames.length, cycleMs]);

  const select = (index: number) => {
    setActive(index);
    setRestart((n) => n + 1);
  };

  return (
    <div
      className={cn("relative aspect-[4/3] w-full overflow-hidden", className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {frames.map((frame, index) => {
        const isActive = index === active;
        return (
          // Opacity lives on the wrapper and the slow zoom on the image, so the
          // two transitions can keep their own durations without colliding.
          <div
            key={frame.src}
            className={cn(
              "absolute inset-0 transition-opacity ease-in-out",
              reducedMotion ? "duration-300" : "duration-1000",
              isActive ? "opacity-100" : "opacity-0"
            )}
            aria-hidden={!isActive}
          >
            <Image
              src={frame.src}
              alt={frame.alt}
              fill
              priority={index === 0}
              sizes="(max-width: 1024px) 100vw, 1024px"
              className={cn(
                "object-cover",
                !reducedMotion && "transition-transform duration-[6000ms]",
                !reducedMotion && "ease-linear",
                isActive && !reducedMotion ? "scale-105" : "scale-100"
              )}
            />
          </div>
        );
      })}

      <div
        role="group"
        aria-label="Choose view"
        className="absolute bottom-3 left-1/2 flex max-w-[calc(100%-1.5rem)] -translate-x-1/2 items-center gap-0.5 rounded-full bg-black/35 p-1 backdrop-blur sm:bottom-4 sm:gap-1"
      >
        {frames.map((frame, index) => {
          const isActive = index === active;
          return (
            <button
              key={frame.src}
              type="button"
              aria-pressed={isActive}
              onClick={() => select(index)}
              className={cn(
                "whitespace-nowrap rounded-full px-2 py-1 text-[11px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-3 sm:text-xs",
                isActive
                  ? "bg-white text-plantista-950"
                  : "text-white/80 hover:text-white"
              )}
            >
              {frame.label}
            </button>
          );
        })}
      </div>

      <p aria-live="polite" className="sr-only">
        {frames[active]?.label}
      </p>
    </div>
  );
}
